'use client';
import { OrderCreatedWebhookDto, RestApiItemDto } from '@order-mage/integration-sdk';
import * as Sentry from '@sentry/nextjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { useCookieSettings } from '@/cookies';
import { CartContext } from '@/entities/cart';
import { customerNotificationsQueryKeyRoot } from '@/entities/customer-notification/api/customer-notifications/constants';
import { OrderProgressAlert } from '@/entities/order/ui/order-progress-alert';
import { getAgeRestriction, isAgeEligible } from '@/features/age-verification/lib/ageVerification';
import { useNewsAutoShow } from '@/features/news';
import { useSyncGuestNewsReadOnAuth } from '@/features/news/useSyncGuestNewsReadOnAuth';
import { QueryApi } from '@/features/query/api';
import { useGetPageUrl } from '@/schema/useGetPageUrl';
import { useRouter } from '@/shared/i18n/navigation';
// side-effect: request Firebase token on page load, so sending order wont take ages
import '@/shared/lib/compat';
import { EventService } from '@/shared/lib/event-service';
import { useRealtime } from '@/shared/lib/realtime/useRealtime';
import { Country } from '@/shared/model/Country';
import { Alert } from '@/shared/ui/alert';
import { ModalService } from '@/shared/ui/modal/ModalService';

const normalizeVariant = (value?: string) => value ?? null;
const areRecordsEqual = (
    left?: Record<string, string | Record<string, number>>,
    right?: Record<string, string | Record<string, number>>,
) => {
    const normalize = (value?: Record<string, string | Record<string, number>>) =>
        Object.fromEntries(
            Object.entries(value ?? {})
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([key, entry]) => {
                    if (entry && typeof entry === 'object') {
                        return [
                            key,
                            Object.fromEntries(Object.entries(entry).sort(([a], [b]) => a.localeCompare(b))),
                        ];
                    }
                    return [key, entry];
                }),
        );
    return JSON.stringify(normalize(left)) === JSON.stringify(normalize(right));
};

export const useEventsSideEffects = () => {
    const router = useRouter();
    const getPageUrl = useGetPageUrl();
    const { itemsFieldArray, formMethods } = useContext(CartContext);
    const queryClient = useQueryClient();
    const customerQuery = useQuery(QueryApi.Customer.queries.Customer);
    const venueQuery = useQuery(QueryApi.Venue.queries.Venue);
    const addItemToFavoriteMutation = useMutation(QueryApi.Customer.mutations.AddItemToFavorite);
    const removeItemFromFavoriteMutation = useMutation(QueryApi.Customer.mutations.RemoveItemFromFavorite);
    const logoutMutation = useMutation(QueryApi.Customer.mutations.Logout);
    const openTrackingOrderAlerts = useRef(new Set());
    const { rawSocket, joinRoom } = useRealtime();

    useEffect(() => {
        const events = [
            EventService.on('router:back', () => {
                router.back();
            }),
            EventService.on('product:view-detail', ({ detail: { slug, variantHash }, defaultPrevented }) => {
                if (defaultPrevented) return;
                const pageUrl = getPageUrl('product-detail', { PRODUCT_SLUG: slug });
                if (!pageUrl) return;

                const url = new URL(pageUrl, window.location.origin);
                if (variantHash && !slug.endsWith(variantHash)) {
                    url.searchParams.set('variant', variantHash);
                }

                router.push(url.pathname + url.search);
            }),
            EventService.on(
                'product:addons-menu:view-detail',
                ({ detail: { productHash, initialQuantity, initialVariant } }) => {
                    ModalService.showPrefab('ProductDetailModal', {
                        hash: productHash,
                        initialQuantity,
                        initialVariant,
                    });
                },
            ),
            EventService.on('cart:product:add-to-cart', async ({ defaultPrevented, detail }) => {
                if (defaultPrevented) return;

                // Check if it's an array of items or single item
                const items = 'items' in detail ? detail.items : [detail];

                // Check age restrictions unless explicitly skipped
                if (!detail.skipAgeVerification) {
                    const menuListsQuery = queryClient.getQueryData(
                        QueryApi.MenuList.queries.MenuLists.queryKey,
                    ) as { items: Record<string, { item: RestApiItemDto }> } | undefined;

                    const highestAgeRestriction = items.reduce<ReturnType<typeof getAgeRestriction>>(
                        (highest, item) => {
                            const itemData = menuListsQuery?.items?.[item.productHash]?.item;
                            const ageRestriction = getAgeRestriction(itemData, Country.CZ);
                            if (!ageRestriction) return highest;
                            if (!highest || ageRestriction.age > highest.age) return ageRestriction;
                            return highest;
                        },
                        null,
                    );

                    if (highestAgeRestriction) {
                        const customer = customerQuery.data;
                        const isSignedIn = !!customer;
                        const hasBirthDate = !!customer?.birthDate;

                        // Scenario 1: Not signed in
                        if (!isSignedIn) {
                            const props = {
                                age: highestAgeRestriction.age,
                                onConfirm: () => {
                                    EventService.trigger('cart:product:add-to-cart', {
                                        ...detail,
                                        skipAgeVerification: true,
                                    });
                                },
                            };
                            await ModalService.showPrefab('AgeVerificationModal', props);
                            return;
                        }
                        // Scenario 2: Signed in but no birthdate
                        if (!hasBirthDate) {
                            await ModalService.showPrefab('AgeVerificationModal', {
                                age: highestAgeRestriction.age,
                                onConfirm: () => {
                                    ModalService.showPrefab('EditAccountDetailsModal', {});
                                },
                            });
                            return;
                        }

                        // Check if user meets age requirement (Scenario 4: eligible)
                        const isEligible = isAgeEligible(customer.birthDate, highestAgeRestriction.age);

                        // Scenario 3: Underage - show blocking modal
                        if (!isEligible) {
                            await ModalService.showPrefab('UnderAgeVerificationModal', {
                                age: highestAgeRestriction.age,
                            });
                            return;
                        }

                        // Scenario 4: Eligible - continue to add items (no return, falls through)
                    }
                }

                items.forEach(({ id, productHash, quantity, variant, addons, memberVariants, note }) => {
                    const trimmedNote = note?.trim() || undefined;
                    if (id) {
                        const index = itemsFieldArray.fields.findIndex((field) => field._id === id);
                        if (index !== -1) {
                            itemsFieldArray.update(index, {
                                hash: productHash,
                                quantity,
                                variant,
                                addons,
                                memberVariants:
                                    memberVariants ?? itemsFieldArray.fields[index].memberVariants,
                                note: trimmedNote,
                            });
                            return;
                        }
                    }
                    const normalizedVariant = normalizeVariant(variant);
                    const matchingIndex = itemsFieldArray.fields.findIndex(
                        (field) =>
                            field.hash === productHash &&
                            normalizeVariant(field.variant) === normalizedVariant &&
                            areRecordsEqual(field.addons, addons) &&
                            areRecordsEqual(field.memberVariants, memberVariants) &&
                            (field.note ?? '') === (trimmedNote ?? ''),
                    );
                    if (matchingIndex !== -1) {
                        itemsFieldArray.update(matchingIndex, {
                            ...itemsFieldArray.fields[matchingIndex],
                            quantity: itemsFieldArray.fields[matchingIndex].quantity + quantity,
                        });
                    } else {
                        itemsFieldArray.append({
                            hash: productHash,
                            quantity,
                            variant,
                            addons,
                            memberVariants,
                            note: trimmedNote,
                        });
                    }
                });

                // Show modal with all items unless suppressed
                if (!detail.suppressModal) {
                    if (items.length === 1) {
                        // Single item - use existing modal format
                        const { productHash, quantity, variant, addons, memberVariants } = items[0];
                        ModalService.showPrefab('AddedToCartModal', {
                            hash: productHash,
                            variant: variant || null,
                            addons: addons || {},
                            memberVariants,
                            quantity,
                        });
                    } else {
                        // Multiple items - pass array
                        ModalService.showPrefab('AddedToCartModal', {
                            items: items.map((item) => ({
                                hash: item.productHash,
                                variant: item.variant || null,
                                addons: item.addons || {},
                                memberVariants: item.memberVariants,
                                quantity: item.quantity,
                            })),
                        });
                    }
                }
            }),
            EventService.on(
                'cart:product:change-quantity',
                ({ defaultPrevented, detail: { id, quantity } }) => {
                    if (defaultPrevented || quantity < 1) return;
                    const index = itemsFieldArray.fields.findIndex((field) => field._id === id);
                    if (index === -1) return;
                    // setValue keeps useFieldArray `_id` stable. `update()` regenerates the id and
                    // remounts the cart row — NumberFlow then snaps instead of animating.
                    formMethods.setValue(`items.${index}.quantity`, quantity, { shouldDirty: true });
                },
            ),
            EventService.on('cart:product:remove-from-cart', ({ defaultPrevented, detail: { id } }) => {
                if (defaultPrevented) return;
                const index = itemsFieldArray.fields.findIndex((field) => field._id === id);
                if (index === -1) return;
                itemsFieldArray.remove(index);
            }),
            EventService.on('cart:view-detail', ({ defaultPrevented }) => {
                if (defaultPrevented) return;
                const pageUrl = getPageUrl('cart');
                if (!pageUrl) return;
                router.push(pageUrl);
            }),
            EventService.on(
                'phone-verification:start',
                ({ defaultPrevented, detail: { telephone, nextRetryAt, onVerify, fallbackToLocal } }) => {
                    if (defaultPrevented) return;
                    ModalService.showPrefab('VerifyPhoneNumberModal', {
                        phoneNumber: telephone,
                        nextRetryAt,
                        onVerify,
                        fallbackToLocal,
                    });
                },
            ),
            EventService.on('phone-verification:finish', ({ defaultPrevented, detail: { modalId } }) => {
                if (defaultPrevented) return;
                if (modalId !== undefined) {
                    ModalService.hide(modalId);
                }
            }),
            EventService.on('account:auth:register', () => {}),
            EventService.on('account:auth:login', () => {}),
            EventService.on('account:auth:logout', async ({ defaultPrevented }) => {
                if (defaultPrevented || logoutMutation.isPending) return;
                await logoutMutation.mutateAsync();
            }),
            EventService.on('account:view-detail', ({ defaultPrevented }) => {
                if (defaultPrevented) return;
                ModalService.showPrefab('MyAccountModal', {});
            }),
            EventService.on('account:auth:view-detail', ({ defaultPrevented }) => {
                if (defaultPrevented) return;
                ModalService.showPrefab('LoginModal', {});
            }),
            EventService.on('account:favorite-items:view-detail', ({ defaultPrevented }) => {
                if (defaultPrevented) return;
                if (!customerQuery.data) {
                    EventService.trigger('account:auth:view-detail');
                } else {
                    ModalService.showPrefab('FavoriteItemsModal', {});
                }
            }),
            EventService.on('account:product:add-to-favorite', ({ defaultPrevented, detail: { id } }) => {
                if (defaultPrevented) return;
                addItemToFavoriteMutation.mutate({ itemId: id });
            }),
            EventService.on(
                'account:product:remove-from-favorite',
                ({ defaultPrevented, detail: { id } }) => {
                    if (defaultPrevented) return;
                    removeItemFromFavoriteMutation.mutate({ itemId: id });
                },
            ),
            EventService.on('privacy-policy:view-detail', ({ defaultPrevented, detail: { onAccept } }) => {
                if (defaultPrevented) return;
                ModalService.showPrefab('PrivacyPolicyModal', { onAccept });
            }),
            EventService.on(
                'terms-and-conditions:view-detail',
                ({ defaultPrevented, detail: { onAccept } }) => {
                    if (defaultPrevented) return;
                    ModalService.showPrefab('TermsAndConditionsModal', { onAccept });
                },
            ),
            EventService.on(
                'register-terms-and-conditions:view-detail',
                ({ defaultPrevented, detail: { onAccept } }) => {
                    if (defaultPrevented) return;
                    ModalService.showPrefab('RegisterTermsAndConditionsModal', { onAccept });
                },
            ),
            EventService.on('order:view-alert-tracking', ({ defaultPrevented, detail: { orderId } }) => {
                if (defaultPrevented) return;
                if (openTrackingOrderAlerts.current.has(orderId)) {
                    return;
                }
                toast(({ closeToast }) => <OrderProgressAlert orderId={orderId} onClose={closeToast} />, {
                    autoClose: false,
                    draggable: false,
                    onClose: () => {
                        openTrackingOrderAlerts.current.delete(orderId);
                    },
                });
                openTrackingOrderAlerts.current.add(orderId);
            }),
            EventService.on('cookies:view-detail', ({ defaultPrevented }) => {
                if (defaultPrevented) return;
                ModalService.showPrefab('CookiesSettingsModal');
            }),
            EventService.on('cookies:update-consents', ({ defaultPrevented, detail: { data } }) => {
                if (defaultPrevented) return;
                useCookieSettings.getState().setCookieSettings(data);
            }),
            EventService.on(
                'notifications:show',
                ({ defaultPrevented, detail: { type, title, description } }) => {
                    if (defaultPrevented) return;
                    toast(({ closeToast }) => (
                        <Alert type={type} heading={title} description={description} onClose={closeToast} />
                    ));
                },
            ),
        ];

        return () => {
            events.forEach((unsubscribe) => unsubscribe());
        };
    }, [itemsFieldArray, formMethods, customerQuery.data]);

    useEffect(() => {
        if (!venueQuery.data?.apsId || !rawSocket) return;

        const apsId = venueQuery.data.apsId;

        const connectToApsRoom = () => {
            joinRoom(`aps:${apsId}`);
        };
        const invalidateMenuLists = () =>
            queryClient.invalidateQueries({ queryKey: QueryApi.MenuList.queries.MenuLists.queryKey });
        const invalidatePaymentMethods = () =>
            queryClient.invalidateQueries({
                queryKey: QueryApi.PaymentMethods.queries.PaymentMethods.queryKey,
            });
        const invalidateDeliveryMethods = () =>
            queryClient.invalidateQueries({
                queryKey: QueryApi.DeliveryMethods.queries.DeliveryMethods.queryKey,
            });
        const invalidateVenue = () =>
            queryClient.invalidateQueries({ queryKey: QueryApi.Venue.queries.Venue.queryKey });
        const invalidateVenueOpeningHours = () =>
            queryClient.invalidateQueries({ queryKey: QueryApi.Venue.queries.OpeningHours.queryKey });
        const invalidateCustomerNotifications = () =>
            queryClient.invalidateQueries({ queryKey: [...customerNotificationsQueryKeyRoot] });
        const handleOutageChange = () =>
            queryClient.invalidateQueries({ queryKey: QueryApi.Outages.queries.Outages.queryKey });
        const handleApiPartnerSettingsChange = () => {
            queryClient.invalidateQueries({ queryKey: QueryApi.Venue.queries.Venue.queryKey });
            queryClient.invalidateQueries({ queryKey: QueryApi.MenuList.queries.MenuLists.queryKey });
            queryClient.invalidateQueries({
                queryKey: QueryApi.PaymentMethods.queries.PaymentMethods.queryKey,
            });
            queryClient.invalidateQueries({
                queryKey: QueryApi.DeliveryMethods.queries.DeliveryMethods.queryKey,
            });
        };
        // Shared by both order events below, so the two can never drift apart on what they
        // invalidate. The id comes from an untrusted socket payload — never assume its shape.
        const invalidateOrder = (orderId: string | undefined) => {
            if (orderId) {
                queryClient.invalidateQueries({ queryKey: QueryApi.Order.queries.Order(orderId).queryKey });
            }
            queryClient.invalidateQueries({ queryKey: QueryApi.Customer.queries.Orders.queryKey });
        };
        const handleOrderUpdate = (data: OrderCreatedWebhookDto['order']) => invalidateOrder(data?.id);
        // A payment settled on a terminal moves the order without any `order:updated` webhook —
        // the eshop patches the status itself and announces it as `order:status:updated`. Without
        // this the tracked-order indicator kept showing "awaiting payment" until a reload.
        const handleOrderStatusUpdate = (data: { orderId?: string } | undefined) =>
            invalidateOrder(data?.orderId);

        const removeCartItemsByHashes = (hashes: string[]) => {
            if (!hashes.length) return;
            const hashSet = new Set(hashes);
            // Remove items from cart that match any of the removed hashes
            // Check both the main item hash and variant hash
            const indicesToRemove = itemsFieldArray.fields
                .map((field, index) => {
                    const shouldRemove =
                        hashSet.has(field.hash) || (field.variant && hashSet.has(field.variant));
                    return shouldRemove ? index : -1;
                })
                .filter((index) => index !== -1)
                .reverse(); // Remove from end to preserve indices

            indicesToRemove.forEach((index) => {
                itemsFieldArray.remove(index);
            });
        };

        const handleServerEvent = (event: { type: string; payload: unknown }) => {
            try {
                switch (event.type) {
                    case 'menu_list:created':
                    case 'menu_list:updated': {
                        invalidateMenuLists();
                        // Remove cart items that were deleted from the menu
                        const payload = event.payload as { removedHashes?: string[] } | undefined;
                        if (payload?.removedHashes?.length) {
                            removeCartItemsByHashes(payload.removedHashes);
                        }
                        break;
                    }
                    case 'menu_list:published':
                    case 'menu_list:archived':
                    // Availability boundary reached (e.g. daily menu opens at 11:00) —
                    // re-evaluate so an already-open tab shows/hides the menu without reload.
                    case 'menu_list:transition': {
                        invalidateMenuLists();
                        break;
                    }
                    case 'delivery_method:created':
                    case 'delivery_method:deleted':
                    case 'delivery_method:updated':
                    case 'delivery_method:published':
                    case 'delivery_method:transition': {
                        invalidateDeliveryMethods();
                        break;
                    }
                    case 'payment_method:created':
                    case 'payment_method:deleted':
                    case 'payment_method:updated':
                    case 'payment_method:published':
                    case 'payment_method:transition': {
                        invalidatePaymentMethods();
                        break;
                    }
                    case 'venue:updated': {
                        invalidateVenue();
                        break;
                    }
                    case 'venue_opening_hours:created':
                    case 'venue_opening_hours:updated':
                    case 'venue_opening_hours:transition': {
                        invalidateVenueOpeningHours();
                        break;
                    }
                    case 'outage:created':
                    case 'outage:updated':
                    case 'outage:archived':
                    case 'outage:transition': {
                        handleOutageChange();
                        break;
                    }
                    case 'api_partner_settings:updated': {
                        handleApiPartnerSettingsChange();
                        break;
                    }
                    case 'order:updated': {
                        handleOrderUpdate(event.payload as OrderCreatedWebhookDto['order']);
                        break;
                    }
                    case 'order:status:updated': {
                        handleOrderStatusUpdate(event.payload as { orderId?: string } | undefined);
                        break;
                    }
                    case 'customer_notification:created':
                    case 'customer_notification:updated':
                    case 'customer_notification:deleted':
                    case 'customer_notification:archived':
                    case 'customer_notification:synced': {
                        invalidateCustomerNotifications();
                        break;
                    }
                }
            } catch (error) {
                // A malformed socket event must not kill the listener for all future events.
                Sentry.captureException(error);
            }
        };

        rawSocket.on('connect', connectToApsRoom);

        // TODO: Connect to orders secret tokens rooms
        rawSocket.on('server:event', handleServerEvent);

        return () => {
            rawSocket.off('server:event', handleServerEvent);
            rawSocket.offAny();
        };
    }, [venueQuery.data?.apsId, rawSocket, joinRoom, queryClient, itemsFieldArray]);
};

export const EventsSideEffects = () => {
    useEventsSideEffects();
    useSyncGuestNewsReadOnAuth();
    useNewsAutoShow();
    return null;
};
